import { Injectable } from '@angular/core';

/**
 * DPoP (RFC 9449) client side — replay-attack protection.
 *
 * - Generates a NON-EXTRACTABLE ECDSA P-256 keypair via WebCrypto. The private
 *   key can never be read (not by this code, not by DevTools, not by injected
 *   XSS script) — it can only be *used* to sign. There is NO pre-shared secret
 *   anywhere in the bundle.
 * - The keypair is persisted in IndexedDB (CryptoKey objects are structured-
 *   cloneable while staying non-extractable) so it survives page reloads and is
 *   shared across tabs. If IndexedDB is unavailable (some private-browsing
 *   modes) an in-memory pair is used for the life of the page.
 * - Every request gets a fresh proof JWT: htm (method), htu (URL), iat
 *   (timestamp), jti (unique nonce → server-side replay cache), ath (SHA-256 of
 *   the access token → binds the proof to that exact token).
 *
 * The proof is attached by dpopInterceptor — services never call this directly.
 */
@Injectable({ providedIn: 'root' })
export class DpopService {
  private static readonly DB_NAME = 'labour-dpop';
  private static readonly STORE = 'keys';
  private static readonly KEY_ID = 'signing-keypair';

  private keyPairPromise: Promise<CryptoKeyPair> | null = null;
  private publicJwkPromise: Promise<JsonWebKey> | null = null;

  /** WebCrypto needs a secure context (https or localhost). */
  get isSupported(): boolean {
    return typeof crypto !== 'undefined' && Boolean(crypto.subtle);
  }

  /** Create a fresh DPoP proof JWT for one specific request. */
  async createProof(method: string, url: string, accessToken?: string): Promise<string> {
    const { privateKey } = await this.getKeyPair();
    const jwk = await this.getPublicJwk();

    const header = { typ: 'dpop+jwt', alg: 'ES256', jwk };
    const payload: Record<string, unknown> = {
      htm: method.toUpperCase(),
      htu: url.split('#')[0].split('?')[0], // RFC 9449: no query/fragment
      iat: Math.floor(Date.now() / 1000),
      jti: this.randomJti(), // unique per request — the replay nonce
    };
    if (accessToken) {
      payload['ath'] = this.b64url(
        new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(accessToken))),
      );
    }

    const enc = (o: unknown) => this.b64url(new TextEncoder().encode(JSON.stringify(o)));
    const signingInput = `${enc(header)}.${enc(payload)}`;

    // WebCrypto ECDSA outputs raw r||s (64 bytes) — exactly JWS ES256 format.
    const signature = await crypto.subtle.sign(
      { name: 'ECDSA', hash: 'SHA-256' },
      privateKey,
      new TextEncoder().encode(signingInput),
    );

    return `${signingInput}.${this.b64url(new Uint8Array(signature))}`;
  }

  // ---------------------------------------------------------------------------
  // Keypair lifecycle
  // ---------------------------------------------------------------------------

  private getKeyPair(): Promise<CryptoKeyPair> {
    this.keyPairPromise ??= this.loadOrCreateKeyPair();
    return this.keyPairPromise;
  }

  private getPublicJwk(): Promise<JsonWebKey> {
    this.publicJwkPromise ??= this.getKeyPair().then(async ({ publicKey }) => {
      const jwk = await crypto.subtle.exportKey('jwk', publicKey); // public part only
      return { kty: jwk.kty, crv: jwk.crv, x: jwk.x, y: jwk.y } as JsonWebKey;
    });
    return this.publicJwkPromise;
  }

  private async loadOrCreateKeyPair(): Promise<CryptoKeyPair> {
    try {
      const stored = await this.idbGet<CryptoKeyPair>(DpopService.KEY_ID);
      if (stored?.privateKey && stored?.publicKey) {
        return stored;
      }
    } catch {
      // IndexedDB unavailable/broken — fall through to a fresh in-memory pair.
    }

    const pair = await crypto.subtle.generateKey(
      { name: 'ECDSA', namedCurve: 'P-256' },
      /* extractable: */ false, // private key is sealed inside the browser
      ['sign'],
    );

    try {
      await this.idbSet(DpopService.KEY_ID, pair);
    } catch {
      // Persistence failed — the pair still works for this page's lifetime.
    }
    return pair;
  }

  // ---------------------------------------------------------------------------
  // Minimal IndexedDB helpers (no external dependency)
  // ---------------------------------------------------------------------------

  private openDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(DpopService.DB_NAME, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(DpopService.STORE)) {
          req.result.createObjectStore(DpopService.STORE);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  private async idbGet<T>(key: string): Promise<T | undefined> {
    const db = await this.openDb();
    try {
      return await new Promise<T | undefined>((resolve, reject) => {
        const tx = db.transaction(DpopService.STORE, 'readonly');
        const req = tx.objectStore(DpopService.STORE).get(key);
        req.onsuccess = () => resolve(req.result as T | undefined);
        req.onerror = () => reject(req.error);
      });
    } finally {
      db.close();
    }
  }

  private async idbSet(key: string, value: unknown): Promise<void> {
    const db = await this.openDb();
    try {
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(DpopService.STORE, 'readwrite');
        tx.objectStore(DpopService.STORE).put(value, key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    } finally {
      db.close();
    }
  }

  // ---------------------------------------------------------------------------

  private randomJti(): string {
    if (typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return this.b64url(bytes);
  }

  private b64url(bytes: Uint8Array): string {
    let bin = '';
    bytes.forEach((b) => (bin += String.fromCharCode(b)));
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
}
